import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useCurrentPartner } from 'services/repositories/partners/partners';
import { routeToPage } from 'support/helpers/navigation/navigation';
import { DocumentType, TransactionsRoutes } from 'support/types';
import { BasePopup } from 'components/Display/BasePopup/BasePopup';
import { Button } from 'components/Form/Button';
import { Translate } from 'components/Translate/Translate';

import { ActionConfig, MessageV2DTO } from '../components/types';

export const useEditAndReprocessAction = (enabledDocumentTypes: Array<DocumentType>): ActionConfig => {
  const navigate = useNavigate();
  const { data: currentPartner } = useCurrentPartner();
  const [isModalOpen, setIsModalOpen] = useState(false);

  /**
   * i18n
   * t('transactions:show.actionButtons.editAndReprocess')
   */
  return {
    label: 'transactions:show.actionButtons.editAndReprocess',
    analyticsId: 'transactions:edit_and_reprocess',
    group: 'resend',
    onClick: () => setIsModalOpen(true),
    test: (message: MessageV2DTO) =>
      message.sender_partner?.id === currentPartner?.id && enabledDocumentTypes.includes(message.type as DocumentType),
    renderModal: (message) => (
      <EditAndReprocessModal
        open={isModalOpen}
        setOpen={setIsModalOpen}
        onConfirm={() => navigate(routeToPage(TransactionsRoutes.reprocess, { id: message.id }))}
      />
    ),
  };
};

type EditAndReprocessModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  onConfirm: () => void;
};

const EditAndReprocessModal = ({ open, setOpen, onConfirm }: EditAndReprocessModalProps) => {
  const { t } = useTranslation();

  return (
    <BasePopup open={open} setOpen={setOpen}>
      <BasePopup.Body>
        <h1 className="text-lg font-semibold text-gray-900">
          {t('transactions:show.actionButtons.editAndReprocessConfirmationModal.title')}
        </h1>
        <div className="mt-2 flex flex-col gap-6">
          <p className="text-sm text-gray-500">
            {t('transactions:show.actionButtons.editAndReprocessConfirmationModal.description')}
          </p>
          <div className="flex items-center justify-end gap-2">
            <Button analyticsId="edit_and_reprocess_cancel" variant="secondary" onClick={() => setOpen(false)}>
              <Translate i18nKey="common:cancel" />
            </Button>
            <Button analyticsId="edit_and_reprocess_confirm" variant="primary" onClick={onConfirm}>
              <Translate i18nKey="transactions:show.actionButtons.editAndReprocessConfirmationModal.confirm" />
            </Button>
          </div>
        </div>
      </BasePopup.Body>
    </BasePopup>
  );
};
